import React from 'react';
import {
  AccountantInfoTooltip,
  PLATFORM_OFFER_POLICY_TOOLTIP,
} from './AccountantInfoTooltip';
import {
  formatAccountantCurrency,
  OfferBreakdownEntry,
  resolveOfferBreakdown,
} from '../../utils/accountantOfferBreakdown';

export const PlatformOfferCell: React.FC<{ entry: OfferBreakdownEntry }> = ({ entry }) => {
  const breakdown = resolveOfferBreakdown(entry);

  if (!breakdown.hasOffer) {
    return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>;
  }

  return (
    <div className="text-sm">
      <div className="font-semibold text-emerald-700 dark:text-emerald-400">
        −{formatAccountantCurrency(breakdown.offerDiscount)}
      </div>
      {breakdown.offerDiscountRate > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {breakdown.offerDiscountRate.toFixed(0)}% off
        </div>
      )}
      {breakdown.offerAbsorbed > 0.009 && (
        <div className="text-xs text-amber-700 dark:text-amber-400">
          Platform absorbed {formatAccountantCurrency(breakdown.offerAbsorbed)}
        </div>
      )}
    </div>
  );
};

export const ListPriceCell: React.FC<{ entry: OfferBreakdownEntry }> = ({ entry }) => {
  const breakdown = resolveOfferBreakdown(entry);
  if (breakdown.listPrice <= breakdown.customerPaid + 0.009) {
    return (
      <span className="text-sm text-gray-900 dark:text-white">
        {formatAccountantCurrency(breakdown.customerPaid)}
      </span>
    );
  }

  return (
    <div className="text-sm">
      <div className="font-medium text-gray-900 dark:text-white">
        {formatAccountantCurrency(breakdown.listPrice)}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Paid {formatAccountantCurrency(breakdown.customerPaid)}
      </div>
    </div>
  );
};

export const CommissionBreakdownCell: React.FC<{ entry: OfferBreakdownEntry }> = ({ entry }) => {
  const breakdown = resolveOfferBreakdown(entry);

  if (breakdown.baseCommission <= 0 && breakdown.platformCommission <= 0) {
    return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>;
  }

  if (breakdown.hasOffer && breakdown.baseCommission > breakdown.platformCommission + 0.009) {
    return (
      <div className="text-sm">
        <div className="font-medium text-gray-900 dark:text-white">
          {formatAccountantCurrency(breakdown.platformCommission)}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Gross {formatAccountantCurrency(breakdown.baseCommission)} − offer
        </div>
      </div>
    );
  }

  return (
    <span className="text-sm font-medium text-gray-900 dark:text-white">
      {formatAccountantCurrency(breakdown.platformCommission)}
    </span>
  );
};

export const SettlementCalcHint: React.FC<{ entry: OfferBreakdownEntry; className?: string }> = ({
  entry,
  className = '',
}) => {
  const b = resolveOfferBreakdown(entry);
  if (!b.hasOffer && b.platformCommission <= 0) return null;

  const parts: string[] = [];
  if (b.listPrice > b.customerPaid + 0.009) {
    parts.push(`List ${formatAccountantCurrency(b.listPrice)}`);
  }
  parts.push(`Paid ${formatAccountantCurrency(b.customerPaid)}`);
  if (b.hasOffer) {
    parts.push(`Offer −${formatAccountantCurrency(b.offerDiscount)}`);
  }
  if (b.platformCommission > 0) {
    parts.push(`Platform comm. −${formatAccountantCurrency(b.platformCommission)}`);
  }
  parts.push(`Net ${formatAccountantCurrency(b.netPayable)}`);

  return (
    <p className={`text-xs text-gray-500 dark:text-gray-400 ${className}`}>
      {parts.join(' → ')}
    </p>
  );
};

export const OfferSummaryBanner: React.FC<{
  totalOfferDiscount: number;
  totalOfferAbsorbed: number;
  totalVendorCommission: number;
  totalPlatformCommission: number;
  offerEntryCount: number;
}> = ({
  totalOfferDiscount,
  totalOfferAbsorbed,
  totalVendorCommission,
  totalPlatformCommission,
  offerEntryCount,
}) => {
  if (offerEntryCount <= 0 && totalOfferDiscount <= 0) return null;

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
          Platform booking offers ({offerEntryCount} {offerEntryCount === 1 ? 'entry' : 'entries'})
        </p>
        <AccountantInfoTooltip
          content={PLATFORM_OFFER_POLICY_TOOLTIP}
          label="Platform offer settlement policy"
          iconClassName="text-emerald-700/70 hover:text-emerald-900 dark:text-emerald-400/80 dark:hover:text-emerald-200"
          panelClassName="w-80"
        />
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-emerald-800 dark:text-emerald-300 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <span className="text-emerald-700/80 dark:text-emerald-400/80">Customer discount</span>
          <p className="font-semibold">{formatAccountantCurrency(totalOfferDiscount)}</p>
        </div>
        <div>
          <span className="text-emerald-700/80 dark:text-emerald-400/80">Absorbed by platform</span>
          <p className="font-semibold">{formatAccountantCurrency(totalOfferAbsorbed)}</p>
        </div>
        <div>
          <span className="text-emerald-700/80 dark:text-emerald-400/80">Gross commission (before offer)</span>
          <p className="font-semibold">{formatAccountantCurrency(totalVendorCommission)}</p>
        </div>
        <div>
          <span className="text-emerald-700/80 dark:text-emerald-400/80">Platform commission (after offer)</span>
          <p className="font-semibold">{formatAccountantCurrency(totalPlatformCommission)}</p>
        </div>
      </div>
    </div>
  );
};
